import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { feeStatusLabel, feeStatusColor, formatMoney } from "../../lib/feeStatus";
import { formatDate, formatDateTime } from "../../lib/dateUtils";

const ACTIONS = [
  { key: "verify", label: "Verify Payment", destructive: false },
  { key: "manual_payment", label: "Manual Payment Entry", destructive: false },
  { key: "payment_not_found", label: "Payment Not Found", destructive: true },
  { key: "incorrect_amount", label: "Record Incorrect Amount", destructive: true },
  { key: "waive", label: "Waive Remaining Balance", destructive: false },
  { key: "mark_free", label: "Mark Free / Complimentary", destructive: false },
  { key: "override", label: "Override Fee", destructive: false },
  { key: "refund", label: "Record Refund", destructive: true },
];

export default function FeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/admin/booking-fees/${id}`).then(({ data }) => setData(data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openAction = (key) => {
    setAction(key);
    if (key === "verify") setForm({ amount: data.remaining_amount || data.final_amount });
    else if (key === "manual_payment") setForm({ amount: data.final_amount, payment_method: "venmo", admin_note: "" });
    else if (key === "payment_not_found") setForm({ notes: "" });
    else if (key === "incorrect_amount") setForm({ expected_amount: data.final_amount, received_amount: "" });
    else if (key === "waive") setForm({ notes: "" });
    else if (key === "mark_free") setForm({ reason: "" });
    else if (key === "override") setForm({ fee_type: "custom", final_amount: data.final_amount, discount_amount: "", override_reason: "" });
    else if (key === "refund") setForm({ status: "refund_sent", amount: data.verified_amount || data.final_amount, method: "venmo", date: "", reference: "" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (action === "verify") {
        await api.put(`/admin/booking-fees/${id}/verify`, null, { params: { amount: Number(form.amount) } });
      } else if (action === "manual_payment") {
        await api.put(`/admin/booking-fees/${id}/manual-payment`, { amount: Number(form.amount), payment_method: form.payment_method, admin_note: form.admin_note });
      } else if (action === "payment_not_found") {
        await api.put(`/admin/booking-fees/${id}/payment-not-found`, null, { params: { notes: form.notes || undefined } });
      } else if (action === "incorrect_amount") {
        await api.put(`/admin/booking-fees/${id}/incorrect-amount`, { expected_amount: Number(form.expected_amount), received_amount: Number(form.received_amount) });
      } else if (action === "waive") {
        await api.put(`/admin/booking-fees/${id}/waive-remaining`, null, { params: { notes: form.notes || undefined } });
      } else if (action === "mark_free") {
        await api.put(`/admin/booking-fees/${id}/mark-free`, null, { params: { reason: form.reason } });
      } else if (action === "override") {
        await api.put(`/admin/booking-fees/${id}/override`, {
          fee_type: form.fee_type, final_amount: Number(form.final_amount),
          discount_amount: form.discount_amount === "" ? null : Number(form.discount_amount),
          override_reason: form.override_reason,
        });
      } else if (action === "refund") {
        await api.post(`/admin/booking-fees/${id}/refund`, {
          status: form.status, amount: form.amount === "" ? null : Number(form.amount),
          method: form.method || null, date: form.date || null, reference: form.reference || null,
        });
      }
      toast.success("Updated");
      setAction(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <p className="font-medium text-midnight-black/60" data-testid="fee-detail-loading">Loading...</p>;

  return (
    <div data-testid="fee-detail-page" className="max-w-4xl">
      <button onClick={() => navigate("/admin/payments")} data-testid="back-to-payments" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-midnight-black/60 hover:text-stranger-orange mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Payments
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="font-extrabold uppercase text-2xl">{data.booking_number} — {data.party_type === "stranger" ? data.stranger_name : data.photographer_name}</h2>
          <p className="text-sm font-medium text-midnight-black/60">{data.session_title} · {formatDate(data.session_date)} · {data.party_type} fee</p>
        </div>
        <span className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full whitespace-nowrap ${feeStatusColor(data.status)}`} data-testid="fee-detail-status">
          {feeStatusLabel(data.status)}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        <div className="border border-soft-gray rounded-xl p-4"><p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-1">Original</p><p className="font-extrabold">{formatMoney(data.original_amount)}</p></div>
        <div className="border border-soft-gray rounded-xl p-4"><p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-1">Discount</p><p className="font-extrabold">{formatMoney(data.discount_amount)}</p></div>
        <div className="border border-soft-gray rounded-xl p-4"><p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-1">Final</p><p className="font-extrabold">{formatMoney(data.final_amount)}</p></div>
        <div className="border border-soft-gray rounded-xl p-4"><p className="text-[10px] font-bold uppercase text-midnight-black/50 mb-1">Remaining</p><p className="font-extrabold">{formatMoney(data.remaining_amount)}</p></div>
      </div>

      {data.override_reason && <p className="text-sm font-medium text-midnight-black/60 mb-8"><strong>Note:</strong> {data.override_reason}</p>}

      <div className="flex flex-wrap gap-2 mb-10" data-testid="fee-actions-bar">
        {ACTIONS.map((a) => (
          <button
            key={a.key}
            data-testid={`fee-action-${a.key}`}
            onClick={() => openAction(a.key)}
            className={`text-[11px] font-bold uppercase tracking-wide px-4 py-2.5 rounded-full border-2 transition-colors ${a.destructive ? "border-red-200 text-red-600 hover:bg-red-50" : "border-soft-gray hover:border-stranger-orange hover:text-stranger-orange"}`}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="mb-10">
        <h3 className="font-extrabold uppercase text-sm mb-4">Reported Payments</h3>
        {data.payments.length === 0 ? (
          <p className="text-sm font-medium text-midnight-black/60" data-testid="no-fee-payments">No payments reported yet.</p>
        ) : (
          <div className="space-y-2" data-testid="fee-payments-list">
            {data.payments.map((p) => (
              <div key={p.id} className="border border-soft-gray rounded-xl p-4 text-sm">
                <p className="font-bold">{p.payment_method?.toUpperCase()} · {formatMoney(p.reported_amount)} {p.sender_username && `· from @${p.sender_username}`}</p>
                <p className="text-xs font-medium text-midnight-black/60">{p.transaction_reference && `Ref: ${p.transaction_reference} · `}Reported {formatDate(p.reported_date)} · Status: {p.verification_status}</p>
                {p.admin_notes && <p className="text-xs text-midnight-black/50 mt-1">Note: {p.admin_notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-extrabold uppercase text-sm mb-4">Audit Trail</h3>
        {data.events.length === 0 ? (
          <p className="text-sm font-medium text-midnight-black/60" data-testid="no-fee-events">No events recorded yet.</p>
        ) : (
          <div className="space-y-2" data-testid="fee-events-list">
            {data.events.map((e) => (
              <div key={e.id} className="border border-soft-gray rounded-xl p-4 text-sm flex justify-between flex-wrap gap-2">
                <span>{e.user_name} ({e.user_role}) — {e.action.replace(/_/g, " ")}{e.amount != null && ` · ${formatMoney(e.amount)}`}{e.notes && ` — ${e.notes}`}</span>
                <span className="text-midnight-black/40 text-xs shrink-0">{formatDateTime(e.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent data-testid="fee-action-modal">
          <DialogHeader>
            <DialogTitle className="font-extrabold uppercase">{ACTIONS.find((a) => a.key === action)?.label}</DialogTitle>
          </DialogHeader>

          {action === "verify" && (
            <Input data-testid="fee-action-amount-input" type="number" placeholder="Verified amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          )}

          {action === "manual_payment" && (
            <div className="space-y-3">
              <Input data-testid="fee-action-amount-input" type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="fee-action-payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="cashapp">Cash App</SelectItem>
                  <SelectItem value="other">Other / Cash</SelectItem>
                </SelectContent>
              </Select>
              <Textarea data-testid="fee-action-note-input" placeholder="Admin note (required)" value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })} />
            </div>
          )}

          {action === "payment_not_found" && (
            <Textarea data-testid="fee-action-notes-input" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          )}

          {action === "incorrect_amount" && (
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="fee-action-expected-input" type="number" placeholder="Expected amount" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} />
              <Input data-testid="fee-action-received-input" type="number" placeholder="Received amount" value={form.received_amount} onChange={(e) => setForm({ ...form, received_amount: e.target.value })} />
            </div>
          )}

          {action === "waive" && (
            <Textarea data-testid="fee-action-notes-input" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          )}

          {action === "mark_free" && (
            <Textarea data-testid="fee-action-reason-input" placeholder="Reason (required)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          )}

          {action === "override" && (
            <div className="space-y-3">
              <Select value={form.fee_type} onValueChange={(v) => setForm({ ...form, fee_type: v })}>
                <SelectTrigger data-testid="fee-action-fee-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="discounted">Discounted</SelectItem>
                  <SelectItem value="waived">Waived</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="comp">Complimentary</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="fee-action-amount-input" type="number" placeholder="New final amount" value={form.final_amount} onChange={(e) => setForm({ ...form, final_amount: e.target.value })} />
              <Input data-testid="fee-action-discount-input" type="number" placeholder="Discount amount (optional)" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} />
              <Textarea data-testid="fee-action-reason-input" placeholder="Override reason (required)" value={form.override_reason} onChange={(e) => setForm({ ...form, override_reason: e.target.value })} />
            </div>
          )}

          {action === "refund" && (
            <div className="space-y-3">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="fee-action-refund-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="refund_required">Refund Required</SelectItem>
                  <SelectItem value="refund_sent">Refund Sent</SelectItem>
                  <SelectItem value="partial_refund_sent">Partial Refund Sent</SelectItem>
                  <SelectItem value="no_refund">No Refund</SelectItem>
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="fee-action-amount-input" type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                <Input data-testid="fee-action-method-input" placeholder="Method (venmo/cashapp)" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="fee-action-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                <Input data-testid="fee-action-reference-input" placeholder="Reference (optional)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" data-testid="fee-action-cancel" onClick={() => setAction(null)} className="rounded-full font-bold">Cancel</Button>
            <Button data-testid="fee-action-submit" disabled={submitting} onClick={submit} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full">
              {submitting ? "Submitting..." : "CONFIRM"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
